import i18n from 'helpers/i18n';
import messages from './messages';
import PageLoader from 'components/PageLoader';
import { useState } from 'react';
import { ReviewInput } from './ReviewInput';
import { ScoresInput } from './ScoresInput';
import { Button, Text } from '@teachstonellc/component-library';
import { VideoPlayer } from 'components/Admin/VideoPlayer';
import { CameraNo } from '@teachstonellc/component-library/dist/icons';
import { useGetNextVideo, QueueVideo } from 'actions/video-obs/double-coders';

interface DoubleCodingQueueProps {
  /** The data for the video to be displayed. */
  video: QueueVideo;

  /** The ID of the video upload, used for claiming or rejecting the video. */
  videoUploadId: number;

  /** The pre-signed URL for the video source. */
  src: string;

  /** Callback function to fetch the next video in the queue. */
  getNextVideo: () => void;

  /** The initial status of the video, either 'review' or 'coding'. */
  initialStatus: 'review' | 'coding';
}

function DoubleCodingQueue({
  src,
  video,
  videoUploadId,
  getNextVideo,
  initialStatus
}: DoubleCodingQueueProps) {
  const [status, setStatus] = useState<
    'review' | 'coding' | 'rejected' | 'completed'
  >(initialStatus);

  function mapAgeLevelGroupSlug(slug: string) {
    switch (slug) {
      case 'pre-kindergarten':
        return 'CLASS 2nd Edition / Pre-K';
      default:
        return slug;
    }
  }

  function onVideoClaimed() {
    setStatus('coding');
  }

  function onScoresSubmitted() {
    setStatus('completed');
  }

  if (status === 'rejected') {
    return (
      <div className="h-full flex flex-col items-center justify-center gap-3 font-inter text-ds-text-primary">
        <div className="bg-ds-white p-6 rounded-xl shadow-ds-resting">
          <Text variant="h3" as="div">
            {i18n.ft(messages.videoRejectedTitle)}
          </Text>

          <Text variant="body2" className="mt-1 text-ds-text-secondary">
            {i18n.ft(messages.videoRejectedDescription)}
          </Text>

          <Button
            fullWidth
            variant="aux"
            className="mt-6"
            onClick={() => {
              getNextVideo();
              setStatus('review');
            }}
          >
            {i18n.ft(messages.reviewNextButton)}
          </Button>
        </div>
      </div>
    );
  }

  if (status === 'completed') {
    return (
      <div className="h-full flex flex-col items-center justify-center gap-3 font-inter text-ds-text-primary">
        <div className="max-w-lg bg-ds-white p-6 rounded-xl shadow-ds-resting">
          <Text variant="h3" as="div">
            {i18n.ft(messages.scoresSubmittedTitle)}
          </Text>

          <Text variant="body2" className="mt-2 text-ds-text-secondary">
            {i18n.ft(messages.scoresSubmittedDescription)}
          </Text>

          <Button
            fullWidth
            variant="aux"
            className="mt-6"
            onClick={() => {
              getNextVideo();
              setStatus('review');
            }}
          >
            {i18n.ft(messages.reviewAnoterVideo)}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <main className="h-full font-inter text-ds-text-primary flex">
      <div className="-mb-8 w-[480px] px-10 py-8 border-r">
        {status === 'review' && (
          <ReviewInput
            videoUploadId={videoUploadId}
            onVideoClaimed={onVideoClaimed}
            onVideoRejected={() => setStatus('rejected')}
          />
        )}

        {status === 'coding' && (
          <ScoresInput
            videoUploadId={videoUploadId}
            onScoresSubmitted={onScoresSubmitted}
            onVideoRejected={() => setStatus('rejected')}
          />
        )}
      </div>

      <div className="-mb-8 flex-1 bg-ds-white">
        <div className="sticky top-0">
          <VideoPlayer source={src} />

          <div className="p-6">
            <div className="p-6 bg-ds-surface-subtle rounded-xl">
              <Text variant="h3">{video.name}</Text>
              <Text variant="body2" className="mt-2 text-ds-text-secondary">
                {video.description}
              </Text>

              <div className="grid grid-cols-2 gap-6 mt-6">
                <div>
                  <Text as="label" variant="h5">
                    {i18n.ft(messages.educator)}
                  </Text>
                  <Text variant="body2">{video.teacher_name}</Text>
                </div>

                <div>
                  <Text as="label" variant="h5">
                    {i18n.ft(messages.languagesSpoken)}
                  </Text>
                  <Text variant="body2">
                    {video.meta_data.languages
                      .map(lang => lang.name)
                      .join(', ')}
                  </Text>
                </div>

                <div>
                  <Text as="label" variant="h5">
                    {i18n.ft(messages.center)}
                  </Text>
                  <Text variant="body2">{video.center_name}</Text>
                </div>

                <div>
                  <Text as="label" variant="h5">
                    {i18n.ft(messages.ageLevel)}
                  </Text>
                  <Text variant="body2">
                    {mapAgeLevelGroupSlug(video.age_level_group_slug)}
                  </Text>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}

function DoubleCodingQueueController() {
  const { data: nextVideo, isLoading, refetch } = useGetNextVideo();

  if (isLoading) {
    return <PageLoader />;
  }

  if (!nextVideo) {
    return (
      <div className="h-full flex flex-col items-center justify-center gap-3 font-inter text-ds-text-primary">
        <CameraNo size={240} className="text-ds-icon-subtle" />

        <Text variant="h1" className="mt-2">
          {i18n.ft(messages.noVideosMessage)}
        </Text>

        <Text variant="body2" className="text-ds-text-secondary">
          {i18n.ft(messages.noVideosDescription)}
        </Text>
      </div>
    );
  }

  return (
    <DoubleCodingQueue
      getNextVideo={refetch}
      videoUploadId={nextVideo.id}
      video={nextVideo.video}
      initialStatus={
        nextVideo.double_code_status === 'in_progress' ? 'coding' : 'review'
      }
      src={nextVideo.pre_signed_url}
    />
  );
}

export default DoubleCodingQueueController;
